import { useEffect, useId, useState } from 'react';

export interface AerodromeSuggestion {
  readonly latitude?: number | null;
  readonly longitude?: number | null;
  readonly icaoCode: string | null;
  readonly iataCode: string | null;
  readonly name: string;
  readonly city: string;
  readonly state: string | null;
  readonly country: string;
}

export function AerodromeLookup({
  label = 'Buscar aeródromo ou heliponto',
  onExpiredSession,
  onSelect,
  selectedLocation = '',
}: {
  readonly label?: string;
  readonly onExpiredSession: () => Promise<void>;
  readonly onSelect: (suggestion: AerodromeSuggestion) => void;
  readonly selectedLocation?: string;
}) {
  const inputId = useId();
  const [query, setQuery] = useState(selectedLocation);
  const [results, setResults] = useState<readonly AerodromeSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string>();
  const [selectedQuery, setSelectedQuery] = useState(selectedLocation);
  const [attempt, setAttempt] = useState(0);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    setQuery(selectedLocation);
    setSelectedQuery(selectedLocation);
    setResults([]);
    setLoading(false);
    setOpen(false);
    setMessage(selectedLocation ? 'Local preenchido. Confira os dados abaixo.' : undefined);
  }, [selectedLocation]);

  useEffect(() => {
    const searched = query.trim();
    if (searched === selectedQuery) return;
    if (searched.length < 2) {
      setResults([]);
      setLoading(false);
      setMessage(undefined);
      return;
    }
    const controller = new AbortController();
    const search = async () => {
      setLoading(true);
      setMessage(undefined);
      try {
        const lookupQuery = searched.split(' — ')[0] || searched;
        const response = await fetch(
          `/v1/reference/aerodromes?q=${encodeURIComponent(lookupQuery)}`,
          {
            credentials: 'include',
            signal: AbortSignal.any([controller.signal, AbortSignal.timeout(10000)]),
          },
        );
        if (response.status === 401) {
          await onExpiredSession();
          return;
        }
        if (!response.ok) throw new Error(String(response.status));
        const body = (await response.json()) as {
          results: AerodromeSuggestion[];
          source?: { fallback?: boolean; retrievedAt?: string };
        };
        if (controller.signal.aborted) return;
        setResults(body.results);
        setMessage(
          body.results.length === 0
            ? 'Nenhum local encontrado. Tente outro nome ou código.'
            : `${body.results.length} opções encontradas.${body.source?.fallback ? ` Cópia de ${body.source.retrievedAt?.slice(0, 10)}.` : ''} Confira a cidade e o estado.`,
        );
      } catch {
        if (!controller.signal.aborted) {
          setMessage('A busca está indisponível. Continue preenchendo manualmente.');
          setResults([]);
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };
    const timeout = window.setTimeout(() => void search(), 350);
    return () => {
      controller.abort();
      window.clearTimeout(timeout);
    };
  }, [onExpiredSession, query, selectedQuery, attempt]);

  return (
    <div
      className="referenceLookup"
      onFocus={() => setOpen(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false);
      }}
    >
      <label htmlFor={inputId}>{label}</label>
      <div className="referenceLookupInput">
        <input
          autoComplete="off"
          id={inputId}
          placeholder="Ex.: SBSP, Congonhas ou São Paulo"
          value={query}
          aria-expanded={open && results.length > 0}
          aria-controls={open && results.length > 0 ? `${inputId}-results` : undefined}
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
            setSelectedQuery('');
            setResults([]);
            setMessage(undefined);
            setLoading(false);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Escape') setOpen(false);
            if (event.key === 'Enter') {
              event.preventDefault();
              setSelectedQuery('');
              setOpen(true);
              setAttempt((value) => value + 1);
            }
          }}
        />
        <button
          type="button"
          disabled={query.trim().length < 2 || loading}
          onClick={() => {
            setOpen(true);
            setSelectedQuery('');
            setAttempt((value) => value + 1);
          }}
        >
          Buscar
        </button>
      </div>
      {open && (loading || message || results.length > 0) ? (
        <div className="referencePopover">
          <small role="status">{loading ? 'Buscando…' : message}</small>
          {results.length > 0 ? (
            <ul
              id={`${inputId}-results`}
              className="referenceResults"
              aria-label="Sugestões de locais"
            >
              {results.map((result) => {
                const code = result.icaoCode ?? result.iataCode;
                return (
                  <li key={`${code ?? 'sem-codigo'}:${result.name}:${result.city}`}>
                    <button
                      type="button"
                      onClick={() => {
                        onSelect(result);
                        const selected = code ? `${code} — ${result.name}` : result.name;
                        setSelectedQuery(selected);
                        setOpen(false);
                        setQuery(selected);
                        setResults([]);
                        setLoading(false);
                        setMessage('Local selecionado. Confira os campos preenchidos.');
                      }}
                    >
                      <strong>
                        {code ? `${code} · ` : ''}
                        {result.name}
                      </strong>
                      <span>
                        {result.city}
                        {result.state ? `, ${result.state}` : ''} · {result.country}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
